import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AudioPlayerCard from '@/domains/traffic-monitoring/components/AudioPlayerCard/AudioPlayerCard';
import { cn } from '@/lib/utils';

interface ChatBubbleProps {
  message?: string;
  image?: string;
  video?: string;
  audio?: string;
  caption?: string;
  time: string;
  isOwn?: boolean;
  isProcessing?: boolean;
}

const ChatBubble = ({
  message,
  image,
  video,
  audio,
  caption,
  time,
  isOwn = false,
  isProcessing = false,
}: ChatBubbleProps) => {
  const { t } = useTranslation();
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  return (
    <div className={cn('mb-4 flex', isOwn ? 'justify-end' : 'justify-start')}>
      <div className="w-full">
        {audio ? (
          <AudioPlayerCard
            title={t('psc>report>voice note')}
            audioUrl={audio}
            isPlaying={isAudioPlaying}
            onPlay={() => setIsAudioPlaying((prev) => !prev)}
          />
        ) : (
          <div
            className={cn(
              'rounded-xl border overflow-hidden',
              isOwn
                ? 'rounded-br-sm border-blue-600 bg-blue-600 text-white '
                : 'rounded-bl-sm bg-db-border bg-db-black'
            )}
          >
            {image && <img src={image} alt="chat" className="h-44 w-full object-cover" />}
            {/* biome-ignore lint/a11y/useMediaCaption: no caption/transcript data exists for WhatsApp media */}
            {video && <video src={video} controls className="h-44 w-full object-cover" />}

            {isProcessing ? (
              <div className="px-3 py-2 flex gap-1 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
              </div>
            ) : (
              (message || caption) && (
                <div className="px-3 py-2">
                  {/* pre-wrap alone only breaks at spaces - a long unbroken
                      token (URL, long id) still overflows the bubble instead
                      of wrapping, so break-words is needed too. */}
                  {message && (
                    <span className="whitespace-pre-wrap break-words title2">{message}</span>
                  )}

                  {caption && (
                    <span className="whitespace-pre-wrap break-words title2">{caption}</span>
                  )}
                </div>
              )
            )}
          </div>
        )}

        <span
          className={cn('mt-1 block text-xs text-gray-500', isOwn ? 'text-right' : 'text-left')}
        >
          {time}
        </span>
      </div>
    </div>
  );
};

export default ChatBubble;
